import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";
import { useTranslate } from "@lobby/ocb-intl";
import { useMemo, useState } from "react";

import { PaymentRest } from "../../../../../entities";

import type { ApiRestTypes } from "@lobby/api-client";

export type RestExtraField = ApiRestTypes.DefinitionMap["PaymentMethods.ExtraField"] & {
  type?: string;
  options?: unknown;
  defaultValue?: string | null;
};

type ExtraFieldPlayerContext = {
  email?: string | null;
  phone?: string | null;
  login?: string;
  memberCard?: string;
};

type SelectOption = { value: string; label: string };

function getSelectOptions(field: RestExtraField): SelectOption[] {
  const raw = field.options;
  if (!Array.isArray(raw)) {
    return [];
  }

  const options: SelectOption[] = [];

  for (const item of raw) {
    if (typeof item === "string") {
      options.push({ value: item, label: item });
      continue;
    }

    if (item && typeof item === "object") {
      const record = item as Record<string, unknown>;
      const value = record.value ?? record.id ?? record.code ?? record.stringId;
      if (value === undefined || value === null) {
        continue;
      }
      const label = record.label ?? record.name ?? value;
      options.push({ value: String(value), label: String(label) });
    }
  }

  return options;
}

function isCountryCodeField(field: RestExtraField): boolean {
  return field.code.toLowerCase().includes("country_code");
}

function resolveFieldKind(field: RestExtraField): "country" | "select" | "email" | "tel" | "text" {
  if (isCountryCodeField(field)) {
    return "country";
  }

  if (field.type === "select" || getSelectOptions(field).length > 0) {
    return "select";
  }

  const code = field.code.toLowerCase();

  if (code.includes("email")) {
    return "email";
  }

  if (code.includes("mobile") || code.includes("phone")) {
    return "tel";
  }

  return "text";
}

export function RestDepositExtraFields({
  fields,
  player,
  formId,
}: {
  fields: RestExtraField[];
  player?: ExtraFieldPlayerContext | null;
  formId?: string;
}) {
  if (fields.length === 0) {
    return null;
  }

  return (
    <div className="billing-information-block space-y-4 mt-2.5">
      {fields.map((field) => (
        <RestDepositExtraField key={field.code} field={field} player={player} formId={formId} />
      ))}
    </div>
  );
}

function RestDepositExtraField({
  field,
  player,
  formId,
}: {
  field: RestExtraField;
  player?: ExtraFieldPlayerContext | null;
  formId?: string;
}) {
  const kind = resolveFieldKind(field);
  const defaultValue = resolveExtraFieldDefaultValue(field, player);

  if (kind === "country") {
    return (
      <RestDepositCountryCodeField field={field} defaultValue={defaultValue} formId={formId} />
    );
  }

  if (kind === "select") {
    return (
      <div>
        <label className="!mb-0">
          {field.name}
          {" *"}
        </label>
        <RestDepositSelectField field={field} defaultValue={defaultValue} formId={formId} />
      </div>
    );
  }

  const inputType = kind === "email" ? "email" : kind === "tel" ? "tel" : "text";

  return (
    <div>
      <label className="!mb-0">
        {field.name}
        {" *"}
      </label>
      <input
        type={inputType}
        name={field.code}
        required
        form={formId}
        defaultValue={defaultValue}
        className="input mt-1"
      />
    </div>
  );
}

function resolveExtraFieldDefaultValue(
  field: RestExtraField,
  player?: ExtraFieldPlayerContext | null,
): string {
  if (field.defaultValue != null && field.defaultValue !== "") {
    return String(field.defaultValue);
  }

  if (!player) {
    return "";
  }

  const code = field.code.toLowerCase();

  if (code.includes("email")) {
    return player.email ?? "";
  }

  if (code.includes("mobile") || code.includes("phone")) {
    return player.phone ?? "";
  }

  if (code === "lobby.payment.account" || code.endsWith(".account")) {
    return player.memberCard || player.login || "";
  }

  return "";
}

function RestDepositCountryCodeField({
  field,
  defaultValue,
  formId,
}: {
  field: RestExtraField;
  defaultValue: string;
  formId?: string;
}) {
  const { data: countriesData, isLoading } = PaymentRest.useCountriesList();
  const countries = countriesData?.countries;
  const resolvedDefaultValue = defaultValue || countriesData?.currentDefault || "";
  const options = useMemo(
    () =>
      (countries ?? []).map((country) => ({
        value: country.code,
        label: country.name,
      })),
    [countries],
  );

  return (
    <div>
      <label className="!mb-0">
        {field.name}
        {" *"}
      </label>
      {isLoading || options.length === 0 ? (
        <div className="select-wrapper mt-1">
          <select disabled className="opacity-50">
            <option value="" />
          </select>
        </div>
      ) : (
        <RestDepositSelectField
          field={field}
          defaultValue={resolvedDefaultValue}
          formId={formId}
          options={options}
          placeholder=""
        />
      )}
    </div>
  );
}

function RestDepositSelectField({
  field,
  defaultValue,
  formId,
  options: optionsOverride,
  placeholder,
}: {
  field: RestExtraField;
  defaultValue: string;
  formId?: string;
  options?: SelectOption[];
  placeholder?: string;
}) {
  const fieldPlaceholder = placeholder ?? field.name;
  const { $t } = useTranslate();
  const options = useMemo(
    () => optionsOverride ?? getSelectOptions(field),
    [field, optionsOverride],
  );
  const [query, setQuery] = useState("");
  const initialOption = useMemo(
    () => options.find((option) => option.value === defaultValue) ?? null,
    [defaultValue, options],
  );
  const [selected, setSelected] = useState<SelectOption | null>(initialOption);

  const filteredOptions =
    query === ""
      ? options
      : options.filter((option) => option.label.toLowerCase().includes(query.toLowerCase()));

  if (options.length <= 8) {
    return (
      <div className="select-wrapper mt-1">
        <select name={field.code} required form={formId} defaultValue={defaultValue}>
          {fieldPlaceholder ? (
            <option value="" disabled>
              {fieldPlaceholder}
            </option>
          ) : null}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <div className="combobox-wrapper mt-1 !max-w-full">
      <input type="hidden" name={field.code} form={formId} value={selected?.value ?? ""} required />
      <Combobox
        immediate
        value={selected}
        onChange={(value) => setSelected(value)}
        onClose={() => setQuery("")}
      >
        <div className="relative">
          <ComboboxInput<SelectOption>
            className="combobox--input"
            placeholder={fieldPlaceholder}
            displayValue={(option) => option?.label}
            onChange={(e) => setQuery(e.target.value)}
            required
          />
          <ComboboxButton className="combobox--button group">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className="size-4"
            >
              <path d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" />
            </svg>
          </ComboboxButton>
        </div>
        <ComboboxOptions anchor="bottom" className="combobox--options">
          {filteredOptions.length === 0 ? (
            <div className="combobox--option px-3 py-2 text-sm opacity-70">
              {$t({ defaultMessage: "No options" })}
            </div>
          ) : (
            filteredOptions.map((option) => (
              <ComboboxOption key={option.value} value={option} className="combobox--option">
                <div className="text-sm/6">{option.label}</div>
              </ComboboxOption>
            ))
          )}
        </ComboboxOptions>
      </Combobox>
    </div>
  );
}
